import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpResponse, HttpHeaders } from '@angular/common/http';
import { HttpClient } from '@angular/common/http';

import { environment } from '../../../environments/environment';
import {
  CheckCnpjResponse,
  PartnerFileUploadResponse,
  PartnerInscriptionData,
  PartnerInscriptionSaveRequest,
  PartnerRequestData,
  PartnerResponse,
  PartnerStatusResponse,
} from '../models/partner.model';
import { unformatCnpj } from '../utils/cnpj.util';

export interface PartnerFormData {
  cnpj: string;
  razaoSocial: string;
  nomeFantasia: string;
  telefone: string;
  email: string;
  cep: string;
  endereco: string;
  numero: string;
  complemento?: string;
  bairro: string;
  cidade: string;
  estado: string;
  descricaoProduto: string;
  urlProduto?: string;
}

export interface PartnerTokenEmailRequest {
  email: string;
}

@Injectable({
  providedIn: 'root',
})
export class PartnerService {
  private readonly API_URL = '/partners';
  private readonly PARTNER_INTEGRATION_API_URL = '/partners-integration';
  private http = inject(HttpClient);


  mapFormDataToRequest(formData: PartnerFormData): PartnerRequestData {
    const request: PartnerRequestData = {
      razaoSocial: formData.razaoSocial.trim(),
      nomeFantasia: formData.nomeFantasia.trim(),
      cnpj: this.cleanDocument(formData.cnpj),
      email: formData.email.trim(),
      street: formData.endereco.trim(),
      zipCode: this.cleanCEP(formData.cep),
      number: formData.numero.trim(),
      complement: formData.complemento?.trim() || '',
      neighborhood: formData.bairro.trim(),
      city: formData.cidade.trim(),
      state: formData.estado,
      phoneNumber: this.cleanPhone(formData.telefone),
      description: formData.descricaoProduto.trim(),
      acceptanceTermsId: 1,
      urlDocumentation: formData.urlProduto?.trim() || 'string',
      userIp: 'string',
      vmSo: 'string',
      locationData: 'string',
      redirectUrl: `${window.location.origin}/cadastro-parceiros/inscricao/comece-aqui`,
    };

    return request;
  }

  createPartner(
    formData: PartnerFormData,
    files: File[]
  ): Observable<HttpResponse<PartnerResponse>> {
    const requestData = this.mapFormDataToRequest(formData);
    const multipartFormData = new FormData();
    
    const jsonString = JSON.stringify(requestData);
    multipartFormData.append('data', jsonString);
    
    files.forEach((file) => {
      multipartFormData.append('file', file);
    });


    const headers = new HttpHeaders({
      'X-API-Key': environment.apiKey,
      'Accept': 'application/json, text/plain, */*',
      'Accept-Language': 'pt-BR,pt;q=0.9'
    });

    return this.http.post<PartnerResponse>(
      `${environment.apiUrl}${this.API_URL}`,
      multipartFormData,
      {
        observe: 'response',
        headers: headers
      }
    );
  }


  sendTokenEmail(email: string): Observable<string> {
    const request: PartnerTokenEmailRequest = {
      email: email.trim(),
    };

    return this.http.post(
      `${environment.apiUrl}${this.API_URL}/email`,
      request,
      { responseType: 'text' }
    );
  }


  getPartnerRequestsByToken(token: string): Observable<PartnerStatusResponse[]> {
    const headers = new HttpHeaders({
      token: token.trim(),
    });

    return this.http.get<PartnerStatusResponse[]>(
      `${environment.apiUrl}${this.API_URL}/requests`,
      { headers }
    );
  }

  checkCnpj(cnpj: string): Observable<CheckCnpjResponse> {
    const clean = unformatCnpj(cnpj);
    return this.http.get<CheckCnpjResponse>(
      `${environment.apiUrl}/partners-integration/${encodeURIComponent(clean)}`
    );
  }

  validateToken(token: string, step: number): Observable<PartnerInscriptionData> {
    const headers = new HttpHeaders({
      token: token.trim(),
    });

    return this.http.get<PartnerInscriptionData>(
      `${environment.apiUrl}/partners-integration`,
      {
        headers,
        params: { step: String(step) },
      }
    );
  }

  uploadPartnerFile(file: File): Observable<PartnerFileUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<PartnerFileUploadResponse>(
      `${environment.apiUrl}${this.PARTNER_INTEGRATION_API_URL}/upload`,
      formData
    );
  }

  saveInscriptionDraft(payload: PartnerInscriptionSaveRequest, step: number): Observable<PartnerInscriptionData> {
    return this.putInscription(payload, true, step);
  }

  submitInscription(payload: PartnerInscriptionSaveRequest, step: number): Observable<PartnerInscriptionData> {
    return this.putInscription(payload, false, step);
  }

  private putInscription(
    payload: PartnerInscriptionSaveRequest,
    draft: boolean,
    step: number
  ): Observable<PartnerInscriptionData> {
    const cnpj = this.cleanDocument(payload.cnpj);
    return this.http.put<PartnerInscriptionData>(
      `${environment.apiUrl}${this.PARTNER_INTEGRATION_API_URL}/${encodeURIComponent(cnpj)}`,
      payload,
      {
        params: { draft: String(draft), step: String(step) },
      }
    );
  }


  private cleanDocument(document: string): string {
    return unformatCnpj(document);
  }

  private cleanPhone(phone: string): string {
    return phone.replace(/\D/g, '');
  }

  private cleanCEP(cep: string): string {
    return cep.replace(/\D/g, '');
  }
}

